import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	ConflictErrorCode,
	type FileConfigFieldError,
	type FileConfigFields,
	type FileConfigListDTO,
	type LogicalFileType,
	type TenantConfigPlane,
	ValidationErrorCode,
} from '@today/types'
import { isAxiosError } from 'axios'
import { fileExchangeService } from '@/services/file-exchange.service'

/** Why a save was refused, in the terms the section shows it (TO-391 spec Contract errors). */
export type FileExchangeSaveError =
	| { kind: 'invalid'; fields: FileConfigFieldError[] }
	| { kind: 'conflict' }
	| { kind: 'users-jit'; provisioningMode: string }
	| { kind: 'failed' }

interface ApiErrorBody {
	error?: { code?: string; details?: { fields?: FileConfigFieldError[]; provisioningMode?: string } }
}

/** Maps a save failure to its kind: field rules, version conflict, the users-file guard, or anything else. */
export const saveErrorOf = (error: unknown): FileExchangeSaveError => {
	const body = isAxiosError<ApiErrorBody>(error) ? error.response?.data?.error : undefined
	if (body?.code === ValidationErrorCode.FILE_CONFIG_INVALID)
		return { kind: 'invalid', fields: body.details?.fields ?? [] }
	if (body?.code === ConflictErrorCode.FILE_CONFIG_VERSION_CONFLICT) return { kind: 'conflict' }
	if (body?.code === ValidationErrorCode.USERS_FILE_REQUIRES_NON_JIT_PROVISIONING) {
		return { kind: 'users-jit', provisioningMode: body.details?.provisioningMode ?? '' }
	}
	return { kind: 'failed' }
}

interface SaveInput {
	fileType: LogicalFileType
	expectedVersion: number
	fields: FileConfigFields
}

/**
 * A tenant's five logical files and the save of one (TO-391 REQ-015), on the
 * route tree of the console's plane. A stored save refetches the list, so the
 * new version shows.
 */
export function useFileExchangeFiles(tenantId: string, plane: TenantConfigPlane) {
	const client = useQueryClient()
	const queryKey = ['file-exchange-files', plane, tenantId] as const
	const query = useQuery<FileConfigListDTO>({ queryKey, queryFn: () => fileExchangeService.list(tenantId, plane) })
	const mutation = useMutation({
		mutationFn: ({ fileType, expectedVersion, fields }: SaveInput) =>
			fileExchangeService.save(tenantId, plane, fileType, { expectedVersion, ...fields }),
		onSuccess: () => client.invalidateQueries({ queryKey }),
	})
	return {
		files: query.data?.items ?? null,
		loadFailed: query.isError,
		reload: () => void query.refetch(),
		save: (fileType: LogicalFileType, expectedVersion: number, fields: FileConfigFields) =>
			mutation.mutate({ fileType, expectedVersion, fields }),
		saving: mutation.isPending,
		saveError: mutation.isError ? saveErrorOf(mutation.error) : null,
		clearSaveError: () => mutation.reset(),
	}
}
